import { useState, useEffect } from 'react';
import { Box, Typography, Chip, Stack, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VideocamIcon from '@mui/icons-material/Videocam';
import { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import { resolveMediaUrl } from '@/utils/firebaseUtils';
import { errorReporter } from '@/services/errorReportingService';
import VideoPreviewModal from './VideoPreviewModal';

interface NotePreviewCardProps {
  note: NoteOut;
  onDeselect: () => void;
}

const NotePreviewCard = ({ note, onDeselect }: NotePreviewCardProps) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setThumbnailUrl(null);
    setVideoUrl(null);

    const loadUrls = async () => {
      const results = await Promise.allSettled([
        note.thumbnail_url ? resolveMediaUrl(note.thumbnail_url) : Promise.reject(new Error('No thumbnail')),
        note.video_url ? resolveMediaUrl(note.video_url) : Promise.reject(new Error('No video')),
      ]);

      if (cancelled) return;

      if (results[0].status === 'fulfilled' && results[0].value) {
        setThumbnailUrl(results[0].value);
      } else if (note.thumbnail_url) {
        errorReporter.expected(
          'Failed to load note thumbnail',
          results[0].status === 'rejected' ? results[0].reason : undefined,
          {
            feature: 'vibeCode',
            operation: 'loadThumbnail',
            noteId: note.id,
          },
        );
      }

      if (results[1].status === 'fulfilled' && results[1].value) {
        setVideoUrl(results[1].value);
      } else if (note.video_url) {
        errorReporter.expected(
          'Failed to load video URL for preview',
          results[1].status === 'rejected' ? results[1].reason : undefined,
          {
            feature: 'vibeCode',
            operation: 'loadVideoUrl',
            noteId: note.id,
          },
        );
      }
    };

    loadUrls();
    return () => {
      cancelled = true;
    };
  }, [note.id, note.thumbnail_url, note.video_url]);

  const truncatedSummary = note.summary
    ? note.summary.length > 200
      ? `${note.summary.slice(0, 200)}...`
      : note.summary
    : null;

  return (
    <>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {/* Thumbnail */}
          <Box
            onClick={() => videoUrl && setIsModalOpen(true)}
            sx={{
              position: 'relative',
              width: 120,
              minWidth: 120,
              height: 80,
              borderRadius: 1.5,
              overflow: 'hidden',
              bgcolor: 'neutral.lightBackground',
              cursor: videoUrl ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover .play-overlay': {
                opacity: 1,
              },
            }}
          >
            {thumbnailUrl ? (
              <Box
                component="img"
                src={thumbnailUrl}
                alt={note.title || 'Video thumbnail'}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <VideocamIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
            )}
            {videoUrl && (
              <Box
                className="play-overlay"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.4)',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                }}
              >
                <PlayCircleOutlineIcon sx={{ fontSize: 36, color: 'white' }} />
              </Box>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {note.title || 'Untitled Note'}
              </Typography>
              <IconButton size="small" onClick={onDeselect} sx={{ ml: 1, mt: -0.5 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            {truncatedSummary && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                {truncatedSummary}
              </Typography>
            )}

            {note.tags && note.tags.length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                {note.tags.slice(0, 4).map((tag) => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" />
                ))}
                {note.tags.length > 4 && <Chip label={`+${note.tags.length - 4}`} size="small" variant="outlined" />}
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>

      <VideoPreviewModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        videoUrl={videoUrl}
        title={note.title || 'Untitled Note'}
        summary={truncatedSummary}
      />
    </>
  );
};

export default NotePreviewCard;
